import { api, USE_MOCK_API } from './api'
import { WordTemplate, TemplateFormData, DocumentGenerationRequest, DocumentGenerationResponse, TemplateCategory } from '../types/wordTemplate'

// Re-export types for convenience
export type { WordTemplate, TemplateCategory }

export interface TemplateUploadInitResponse {
  templateId: string
  s3Key: string
  uploadUrl: string
  fields: Record<string, string>
}

export const templateService = {
  /**
   * Get all templates with optional category filter
   */
  getAll: async (category?: string): Promise<WordTemplate[]> => {
    const params = category ? { category } : {}
    const response = await api.get<WordTemplate[]>('/templates', { params })
    return response.data
  },

  /**
   * Get a single template by ID
   */
  getById: async (id: string): Promise<WordTemplate> => {
    const response = await api.get<WordTemplate>(`/templates/${id}`)
    return response.data
  },

  /**
   * Create a new template
   */
  create: async (data: TemplateFormData): Promise<WordTemplate> => {
    const response = await api.post<WordTemplate>('/templates', data)
    return response.data
  },

  /**
   * Update an existing template
   */
  update: async (id: string, data: Partial<TemplateFormData>): Promise<WordTemplate> => {
    const response = await api.put<WordTemplate>(`/templates/${id}`, data)
    return response.data
  },

  /**
   * Initialize direct S3 upload for template files
   */
  initUpload: async (file: File): Promise<TemplateUploadInitResponse> => {
    const response = await api.post<TemplateUploadInitResponse>('/templates/upload/init', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type || 'application/octet-stream',
    })
    return response.data
  },

  /**
   * Upload file directly to S3 using presigned POST
   */
  uploadToS3: async (uploadUrl: string, fields: Record<string, string>, file: File): Promise<void> => {
    if (USE_MOCK_API || uploadUrl.startsWith('mock://')) {
      return
    }

    const formData = new FormData()
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value)
    })
    formData.append('file', file)

    const response = await fetch(uploadUrl, {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) {
      throw new Error(`S3 upload failed with status ${response.status}`)
    }
  },

  /**
   * Soft delete (archive) a template
   */
  archive: async (id: string): Promise<void> => {
    await api.delete(`/templates/${id}`)
  },

  /**
   * Generate a document from a template
   */
  generateDocument: async (request: DocumentGenerationRequest): Promise<DocumentGenerationResponse> => {
    const response = await api.post<DocumentGenerationResponse>('/templates/generate', request)
    return response.data
  },

  /**
   * Preview a template with sample data
   */
  preview: async (templateId: string, sampleData?: Record<string, any>): Promise<Blob> => {
    const response = await api.post<string>(
      '/templates/preview',
      { templateId, sampleData }
    )
    
    // Convert base64 string to Blob
    const base64Content = response.data
    const byteCharacters = atob(base64Content)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    
    // Determine MIME type
    const mimeType = 'application/vnd.oasis.opendocument.text'
    
    return new Blob([byteArray], { type: mimeType })
  },

  /**
   * Download a document from base64 content
   */
  downloadDocument: (content: string, fileName: string, mimeType?: string): void => {
    try {
      // Validate base64 string
      if (!content || typeof content !== 'string') {
        throw new Error('Invalid content: content must be a non-empty string')
      }
      
      let cleanContent = content.trim()
      if (cleanContent.startsWith('data:')) {
        const base64Idx = cleanContent.indexOf('base64,')
        if (base64Idx !== -1) {
          cleanContent = cleanContent.slice(base64Idx + 'base64,'.length).trim()
        }
      }
      cleanContent = cleanContent.replace(/\s/g, '')

      // Validate base64 format (basic check)
      if (!/^[A-Za-z0-9+/]*={0,2}$/.test(cleanContent)) {
        throw new Error('Invalid base64 content format')
      }
      
      const byteCharacters = atob(cleanContent)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      
      // Determine MIME type from filename or use provided mimeType
      let blobType = mimeType
      if (!blobType) {
        if (fileName.endsWith('.odt')) {
          blobType = 'application/vnd.oasis.opendocument.text'
        } else if (fileName.endsWith('.ott')) {
          blobType = 'application/vnd.oasis.opendocument.text-template'
        } else if (fileName.endsWith('.docx')) {
          blobType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        } else {
          // Default to OpenOffice
          blobType = 'application/vnd.oasis.opendocument.text'
        }
      }
      
      const blob = new Blob([byteArray], { type: blobType })

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error: any) {
      console.error('[templateService] Error downloading document:', error)
      throw new Error(`Failed to download document: ${error.message}`)
    }
  },
}
































